/**
 * Shared utilities for displaying session information.
 *
 * IMPORTANT: These utilities are used by multiple components:
 * - SessionsDialog.tsx (Sessions dialog)
 * - SessionListCard.tsx (Sessions dialog - Pro mode)
 *
 * When modifying display logic here, ensure consistency across all consumers.
 */

import type { Session, SerializedTab } from '../types/sessionTypes';
import type { Connection } from '../../../types/electron';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { safeConnectionName, safeServerText } from '../../../types/safeConnectionDisplay';

// ============================================================================
// Types
// ============================================================================

export type ConnectionInfo = {
  id: string;
  name: string;
  engine: 'mssql' | 'pgsql';
  databases: string[];
  /**
   * ISO timestamp of the connection's last successful connect, when the saved
   * connection recorded one. Undefined for a connection that has never been
   * connected (or whose saved config is gone) — callers must treat the age as
   * "not derivable" rather than inventing one.
   */
  lastConnectedAt?: string;
};

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format an ISO date string as relative time (e.g., "5m ago", "2h ago", "Yesterday")
 */
export function formatTimeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

/**
 * Format an ISO date string as a short calendar date (e.g., "Jul 30") in the
 * user's local timezone. Used for the dated session-name fallback so a
 * session created on a given day keeps that day in its name — distinct from
 * `formatTimeAgo`, which is relative to "now" and changes on every render.
 */
export function formatShortDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ============================================================================
// Connection Info Extraction
// ============================================================================

/**
 * Extract unique connection info from session tabs and connections.
 * Returns connection details with associated databases.
 */
export function getConnectionInfo(
  tabs: SerializedTab[],
  sessionConnections: Array<{ connectionId: string }>,
  savedConnections: Connection[]
): ConnectionInfo[] {
  const connectionMap = new Map<
    string,
    {
      name: string;
      engine: 'mssql' | 'pgsql';
      databases: Set<string>;
      lastConnectedAt?: string;
    }
  >();

  // Add connections from tabs (with database info)
  tabs.forEach((tab) => {
    const connId = tab.connectionInfo?.connectionId;
    if (!connId) return;

    if (!connectionMap.has(connId)) {
      const savedConn = savedConnections.find((c) => c.id === connId);
      const engine = savedConn?.engine === 'pgsql' ? 'pgsql' : 'mssql';
      connectionMap.set(connId, {
        // Security-slice review SBT-05: never a raw tab snapshot or a raw saved alias.
        name:
          safeServerText(tab.connectionInfo?.connectionName) ||
          (savedConn ? safeConnectionName(savedConn) : 'Unknown Connection'),
        engine,
        databases: new Set(),
        lastConnectedAt: savedConn?.lastConnectedAt,
      });
    }

    const database = tab.connectionInfo?.database;
    if (database) {
      connectionMap.get(connId)!.databases.add(database);
    }
  });

  // Add session connections that aren't in tabs yet
  sessionConnections.forEach(({ connectionId }) => {
    if (!connectionMap.has(connectionId)) {
      const savedConn = savedConnections.find((c) => c.id === connectionId);
      if (savedConn) {
        const engine = savedConn.engine === 'pgsql' ? 'pgsql' : 'mssql';
        connectionMap.set(connectionId, {
          name: safeConnectionName(savedConn, 'Unknown Connection'),
          engine,
          databases: new Set(),
          lastConnectedAt: savedConn.lastConnectedAt,
        });
      }
    }
  });

  return Array.from(connectionMap.entries()).map(([id, info]) => ({
    id,
    name: info.name,
    engine: info.engine,
    databases: Array.from(info.databases),
    lastConnectedAt: info.lastConnectedAt,
  }));
}

/**
 * Ids of connections that are connected *right now*.
 *
 * `activeConnections` is a registry, not a liveness list — membership in it
 * does NOT mean connected:
 * - `markConnectionDisconnected` keeps the entry and only flips
 *   `status: 'disconnected'` (that's how a dropped connection stays visible);
 * - `loadSession` seeds Entra connections as disconnected entries before they
 *   have ever connected;
 * - `deleteConnection` never prunes the list.
 *
 * Anything asking "is this connection usable right now?" must go through here.
 */
export function getConnectedConnectionIds(
  activeConnections: Pick<ActiveConnection, 'id' | 'status'>[]
): string[] {
  return activeConnections.filter((conn) => conn.status === 'connected').map((conn) => conn.id);
}

/**
 * Is this session's content actually present in the workspace right now?
 *
 * The Sessions dialog's featured card has two legitimate meanings for its
 * primary action: *resume* (restore the session) and *go back* (just close the
 * dialog, because the session is already the live workspace). Picking the
 * wrong one is user-visible — "go back" on a session that was never restored
 * closes the dialog and restores nothing.
 *
 * The discriminator is the session's own substance:
 * - if the session declares meaningful tabs, it is live only when at least one
 *   of them is currently mounted (tab ids survive serialize/deserialize);
 *   start-page tabs never count — a Start Page is ambient scaffolding the app
 *   creates on its own (e.g. after the startup dialog is dismissed), not
 *   evidence the user's session came back;
 * - a connection-only session (no meaningful tabs) is live when any connection
 *   it declares is currently connected;
 * - a session with neither is not live (and has nothing to restore).
 *
 * Deliberately a union rather than "everything is present": it only reports
 * "not live" when *nothing* of the session is in the workspace, so a mid-work
 * reopen (one tab closed, one connection dropped) never triggers a disruptive
 * re-restore.
 *
 * CONTRACT: `connectedConnectionIds` must be ids of connections that are
 * CONNECTED, not merely present in `activeConnections` — pass
 * `getConnectedConnectionIds(activeConnections)`. Handing this the raw
 * membership list makes a dropped connection look live, which is exactly the
 * silent-no-op the discriminator exists to prevent.
 */
export function isSessionLiveInWorkspace(
  session: Session,
  liveTabIds: string[],
  connectedConnectionIds: string[]
): boolean {
  const meaningfulTabs = session.tabs.filter((tab) => tab.type !== 'start-page');
  if (meaningfulTabs.length > 0) {
    const mounted = new Set(liveTabIds);
    return meaningfulTabs.some((tab) => mounted.has(tab.id));
  }

  const connected = new Set(connectedConnectionIds);
  return session.connections.some((conn) => connected.has(conn.connectionId));
}

// ============================================================================
// Formatting Helpers
// ============================================================================

/**
 * Format a list of databases for compact display
 */
export function formatDatabases(databases: string[]): string {
  if (databases.length === 0) return '';
  if (databases.length <= 2) return databases.join(', ');
  return `${databases.slice(0, 2).join(', ')} +${databases.length - 2}`;
}

/**
 * Format connection names for compact display
 */
export function formatConnections(
  connections: Array<{ name: string; engine: 'mssql' | 'pgsql' }>
): string {
  if (connections.length === 0) return 'No connections';
  if (connections.length === 1) return connections[0].name;
  if (connections.length === 2) return `${connections[0].name}, ${connections[1].name}`;
  return `${connections[0].name} +${connections.length - 1} more`;
}

/**
 * Format storage size in human-readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

/**
 * Get the storage size of persisted sessions in localStorage
 */
export function getPersistedSessionsStorageBytes(): number {
  if (typeof window === 'undefined' || !window.localStorage) return 0;
  const raw = window.localStorage.getItem('sql-studio-session');
  if (!raw) return 0;

  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(raw).length;
  }

  return raw.length;
}
